import { FC, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Component as AboutUs } from "../../../page/AboutUs/AboutUs";
import { Component as ContactsPage } from "../../../page/ContactsPage/ContactsPage";
import { Component as EventsPage } from "../../../page/EventsPage/EventsPage";
import { Component as HomePage } from "../../../page/HomePage/HomePage";
import { HomeBoardVideo } from "../../../page/HomePage/HomeBoardVideo";
import { cn } from "../../lib/cn";
import { ChalkFlipContext } from "./chalk-flip-context";
import "./chalk-board-flip.css";

const DESKTOP_QUERY = "(min-width: 720px)";
const BLOW_MS = 820;
const ARRIVE_MS = 620;

type FaceId = "home" | "afisha" | "team" | "contacts";

function useDesktopFlip() {
  const read = () => window.matchMedia(DESKTOP_QUERY).matches;
  const [enabled, setEnabled] = useState(read);

  useEffect(() => {
    const desktop = window.matchMedia(DESKTOP_QUERY);
    const sync = () => setEnabled(desktop.matches);
    desktop.addEventListener("change", sync);
    return () => desktop.removeEventListener("change", sync);
  }, []);

  return enabled;
}

function decodePath(pathname: string) {
  try {
    return decodeURIComponent(pathname);
  } catch {
    return pathname;
  }
}

function faceFromPath(pathname: string): FaceId {
  const path = decodePath(pathname);
  if (path === "/события" || path === "/events") return "afisha";
  if (path === "/команда" || path === "/aboutus") return "team";
  if (path === "/контакты" || path === "/contacts") return "contacts";
  return "home";
}

function FacePage({ id }: { id: FaceId }) {
  if (id === "afisha") return <EventsPage />;
  if (id === "team") return <AboutUs />;
  if (id === "contacts") return <ContactsPage />;
  return <HomePage />;
}

export const ChalkBoardFlipLayout: FC = () => {
  const enabled = useDesktopFlip();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const currentFace = faceFromPath(pathname);
  const [faceId, setFaceId] = useState<FaceId>(currentFace);
  const [blowing, setBlowing] = useState(false);
  const [arriving, setArriving] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);

  useEffect(() => {
    if (blowing || pendingPath) return;
    if (faceId === currentFace) return;
    setFaceId(currentFace);
  }, [blowing, currentFace, faceId, pendingPath]);

  useEffect(() => {
    if (!blowing || !pendingPath) return;
    const nextPath = pendingPath;
    const timer = window.setTimeout(() => {
      setFaceId(faceFromPath(nextPath));
      setBlowing(false);
      setArriving(true);
      setPendingPath(null);
      navigate(nextPath);
    }, BLOW_MS);
    return () => window.clearTimeout(timer);
  }, [blowing, navigate, pendingPath]);

  useEffect(() => {
    if (!arriving) return;
    const timer = window.setTimeout(() => setArriving(false), ARRIVE_MS);
    return () => window.clearTimeout(timer);
  }, [arriving]);

  if (!enabled) return <Outlet />;

  const turnTo = (path: string) => {
    if (blowing || pendingPath) return;
    const next = faceFromPath(path);
    if (next === faceId) return;
    setArriving(false);
    setBlowing(true);
    setPendingPath(path);
  };

  const scrollable = faceId !== "home";
  const stageClass = cn(
    "chalk-flip",
    blowing && "is-blowing",
    arriving && "is-arriving",
  );
  const clipClass = cn(
    "chalk-flip__face-clip",
    scrollable && "chalk-flip__face-clip--scroll",
  );
  const context = { active: true, face: "front" as const, turning: blowing, turnTo };

  return (
    <div className={stageClass}>
      <div className="chalk-flip__rotor">
        <ChalkFlipContext.Provider value={context}>
          <div className="chalk-flip__face">
            <HomeBoardVideo owner />
            <div className={clipClass}>
              <FacePage id={faceId} />
            </div>
          </div>
        </ChalkFlipContext.Provider>
      </div>
    </div>
  );
};
